// Tolerant matching of free text against admin value lists (PLAN §2): case-insensitive, synonyms, and
// qualifiers kept as a note ("ACTIVE (reduced activity since 2018)" → Active + note "reduced activity since 2018").

import type { EnumValueDef } from './types';

/** Lowercase, accents removed, every run of punctuation/space → one space. */
export const foldText = (s: string): string =>
  s
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim() || s.trim().toLowerCase(); // symbols-only values ("✓", "?") match as written

export interface EnumMatch<V extends Pick<EnumValueDef, 'key' | 'label' | 'synonyms'> = EnumValueDef> {
  /** The matched list value, or null when nothing fits (the original text is then the note). */
  value: V | null;
  /** `exact` = label or key; `synonym`; `qualified` = a value followed by extra words (kept as the note). */
  how: 'exact' | 'synonym' | 'qualified' | null;
  /** Text to keep next to the value, e.g. as a *Camp note*. */
  note: string | null;
}

const namesOf = (v: Pick<EnumValueDef, 'key' | 'label' | 'synonyms'>) => ({
  primary: [foldText(v.label), foldText(v.key.replace(/_/g, ' '))],
  synonyms: v.synonyms.map(foldText).filter(Boolean),
});

/** The original text after its first `words` words, without wrapping punctuation. */
function remainderAfterWords(raw: string, words: number): string | null {
  const re = /[\p{L}\p{N}\p{M}]+/gu;
  let end = 0;
  for (let i = 0; i < words; i++) {
    const m = re.exec(raw);
    if (!m) return null;
    end = m.index + m[0].length;
  }
  const rest = raw
    .slice(end)
    .replace(/^[\s\p{P}\p{S}]+/u, '')
    .replace(/[\s)\]}.,;:—–-]+$/u, '');
  return rest || null;
}

/** Matches free text to a list value. Archived values are skipped. */
export function matchEnumValue<V extends Pick<EnumValueDef, 'key' | 'label' | 'synonyms'> & { archived?: boolean }>(
  values: readonly V[],
  input: string,
): EnumMatch<V> {
  const raw = input.trim();
  const f = foldText(raw);
  if (!f) return { value: null, how: null, note: null };
  const live = values.filter(v => !v.archived);
  for (const v of live) if (namesOf(v).primary.includes(f)) return { value: v, how: 'exact', note: null };
  for (const v of live) if (namesOf(v).synonyms.includes(f)) return { value: v, how: 'synonym', note: null };
  let best: { v: V; name: string } | null = null;
  for (const v of live) {
    const { primary, synonyms } = namesOf(v);
    for (const name of [...primary, ...synonyms])
      if (name && f.startsWith(`${name} `) && (!best || name.length > best.name.length)) best = { v, name };
  }
  if (best) return { value: best.v, how: 'qualified', note: remainderAfterWords(raw, best.name.split(' ').length) };
  return { value: null, how: null, note: raw };
}
